/**
 * Pure helpers for the connection-import flow: analytics buckets, duplicate
 * detection, and building a Jam `Connection` from an import candidate across
 * all five engines. Kept dependency-free so they are cheap to unit-test.
 */
import { v4 as uuidv4 } from 'uuid';
import type { Connection } from '../../../types/electron';
import type { ImportCandidate, ImportEngine } from '../../../types/connectionImport';
import { normalizeKustoClusterUrl } from '../../../core/engines/kustoUrl';

/** Low-cardinality bucket for connection counts (analytics). */
export function countBucket(count: number): '1' | '2-5' | '6-20' | '20+' {
  if (count <= 1) return '1';
  if (count <= 5) return '2-5';
  if (count <= 20) return '6-20';
  return '20+';
}

/**
 * An unsupported candidate whose engine Jam SQL Studio doesn't import *yet*
 * but plans to — surfaced as "coming soon" rather than a flat "not supported".
 * No providers are coming-soon right now (Kusto now imports for real, S8); the
 * next such provider slots in here and the row's badge branch lights back up.
 */
export function isComingSoonProvider(_providerName: string): boolean {
  return false;
}

/** Default port per engine, used when a candidate carries none / '<auto>' (SQL Server keeps '<auto>': `resolvePort`). */
const DEFAULT_PORT: Record<ImportEngine, string> = {
  mssql: '<auto>',
  pgsql: '5432',
  mysql: '3306',
  oracle: '1521',
  sqlite: '',
  kusto: '0', // ADX is HTTPS/REST — the full cluster URL lives in `server`.
  azurestorage: '0', // storage is HTTPS/REST — the endpoints live in azureStorageEndpoints.
};

/** Default Jam authentication label per engine. */
const DEFAULT_AUTH: Record<ImportEngine, Connection['authentication']> = {
  mssql: 'SQL Server authentication',
  pgsql: 'Password',
  mysql: 'Password',
  oracle: 'Password',
  sqlite: 'No password',
  kusto: 'Microsoft Entra ID (Interactive Browser)',
  azurestorage: 'Microsoft Entra ID (Interactive Browser)',
};

/**
 * A candidate is a duplicate when engine + endpoint already match a saved
 * connection — pre-unchecked so re-running the import stays safe. SQLite
 * matches on file path; other engines match on server + database + user.
 */
export function isDuplicateCandidate(
  candidate: ImportCandidate,
  existing: Connection[]
): boolean {
  if (!candidate.supported || !candidate.engine) return false;

  if (candidate.engine === 'sqlite') {
    const file = (candidate.filePath ?? candidate.server).toLowerCase();
    return existing.some(
      (c) =>
        (c.engine ?? 'mssql') === 'sqlite' &&
        (String(c.filePath ?? c.server ?? '')).toLowerCase() === file
    );
  }

  // Kusto servers are URLs — normalize both sides so a trailing-slash / scheme
  // difference doesn't defeat duplicate detection.
  const normServer = (s: string) =>
    candidate.engine === 'kusto' ? normalizeKustoClusterUrl(s).toLowerCase() : s.toLowerCase();
  const server = normServer(candidate.server);
  const database = (candidate.database ?? '').toLowerCase();
  const user = (candidate.user ?? '').toLowerCase();
  return existing.some(
    (c) =>
      (c.engine ?? 'mssql') === candidate.engine &&
      normServer(String(c.server ?? '')) === server &&
      String(c.database ?? '').toLowerCase() === database &&
      String(c.login ?? '').toLowerCase() === user
  );
}

/** Group candidates under their source group path (undefined → "Ungrouped"), keeping order. */
export function groupCandidatesByPath(
  candidates: ImportCandidate[]
): { path: string | undefined; candidates: ImportCandidate[] }[] {
  const groups: { path: string | undefined; candidates: ImportCandidate[] }[] = [];
  const byPath = new Map<string | undefined, ImportCandidate[]>();
  for (const candidate of candidates) {
    const key = candidate.groupPath;
    let bucket = byPath.get(key);
    if (!bucket) {
      bucket = [];
      byPath.set(key, bucket);
      groups.push({ path: key, candidates: bucket });
    }
    bucket.push(candidate);
  }
  return groups;
}

/**
 * Resolve the port for a saved Connection (named-instance/blank → engine default). SQL Server keeps `'<auto>'`, the value
 * the New Connection form leaves: on Windows it lets SQL Tools Service reach a local server over shared memory or named
 * pipes, and a named instance resolves its own port — a pinned 1433 forced TCP and timed out where TCP/IP is off or
 * elsewhere (Windows release pass B1; `electron/connection-import/shared/mssqlImport.ts`). The other engines' automatic
 * port is only their default TCP port, so it is written out as before.
 */
function resolvePort(candidate: ImportCandidate): string {
  if (candidate.port && candidate.port !== '<auto>') return candidate.port;
  const engine = candidate.engine ?? 'mssql';
  return engine === 'mssql' ? '<auto>' : DEFAULT_PORT[engine];
}

/**
 * Build a saved `Connection` from a supported candidate. A password is included
 * only when one is available — either typed during review or prefilled from a
 * documented plaintext file (.pgpass / .my.cnf / pg_service.conf). Otherwise
 * the general on-connect prompt collects it on first Connect.
 */
export function buildConnectionFromCandidate(
  candidate: ImportCandidate,
  password?: string
): Connection {
  const engine = candidate.engine ?? 'mssql';
  const isSqlite = engine === 'sqlite';
  const filePath = candidate.filePath ?? (isSqlite ? candidate.server : undefined);
  const server = isSqlite
    ? filePath ?? candidate.server
    : engine === 'kusto'
      ? normalizeKustoClusterUrl(candidate.server)
      : candidate.server;

  const connection: Connection = {
    id: uuidv4(),
    engine,
    alias: candidate.alias,
    server,
    port: resolvePort(candidate),
    authentication: candidate.authentication ?? DEFAULT_AUTH[engine as ImportEngine],
    login: candidate.user,
    database: candidate.database,
    readOnly: false,
  };

  if (isSqlite && filePath) connection.filePath = filePath;
  if (candidate.ssl) connection.ssl = true;
  if (candidate.oracleServiceName) connection.oracleServiceName = candidate.oracleServiceName;
  if (candidate.oracleUseSid) connection.oracleUseSID = true;
  if (candidate.oracleConnectionStringType) connection.oracleConnectionStringType = candidate.oracleConnectionStringType;
  if (candidate.oracleTnsDescriptor) connection.oracleTnsDescriptor = candidate.oracleTnsDescriptor;
  if (candidate.oracleTnsAlias) connection.oracleTnsAlias = candidate.oracleTnsAlias;
  if (candidate.oracleTnsAdmin) connection.oracleTnsAdmin = candidate.oracleTnsAdmin;
  if (candidate.oracleWalletLocation) connection.oracleWalletLocation = candidate.oracleWalletLocation;
  // Azure Storage: the five fields travel together (spec §4.2).
  if (candidate.azureStorageEndpoints) connection.azureStorageEndpoints = candidate.azureStorageEndpoints;
  if (candidate.azureStorageServices) connection.azureStorageServices = candidate.azureStorageServices;
  if (candidate.azureStorageScope) connection.azureStorageScope = candidate.azureStorageScope;
  if (candidate.azureStorageEmulator) connection.azureStorageEmulator = true;
  if (candidate.azureResourceId) connection.azureResourceId = candidate.azureResourceId;
  if (candidate.azureResourceIdOrigin) connection.azureResourceIdOrigin = candidate.azureResourceIdOrigin;
  if (candidate.encrypt !== undefined) connection.encrypt = candidate.encrypt;
  if (candidate.trustServerCertificate !== undefined) connection.trustServerCertificate = candidate.trustServerCertificate;

  const pw = password && password.trim() ? password : candidate.prefilledPassword;
  if (pw && pw.trim()) connection.password = pw;

  return connection;
}
